import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CCFile, CCFileGallery, useToast } from "@pymblecitizencoders/cc-ui";
import { useEventFiles } from "../../hooks/useEventFiles";
import { EventDoc, EventFile } from "../../types/event";
import {
	DOCUMENT_MIME_TYPES,
	EventDocumentError,
	checkDocument,
} from "../../utils/eventDocument";
import "./eventManager.css";

interface EventDocumentsProps {
	eventId: string;
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * The event's attached documents.
 *
 * cc-ui's CCFileGallery rather than a picker built here: it brings the drop
 * target, the picker, the type tiles, the preview, the download control and the
 * list/grid switch, and it is what every other file surface in the platform
 * will look like.
 *
 * TWO HALVES THAT FAIL DIFFERENTLY, and the hint says so. Uploading is real —
 * `storage/file/` takes the file and keeps it, no API change needed. The LIST
 * is part of the event document, and `EventUpdateRequest` does not declare
 * `Files` yet, so a save drops it and leaves the blob behind. Until that one
 * line lands this is a working uploader whose attachments do not survive a
 * reload, which is worth saying out loud rather than discovering.
 *
 * `list` FIRST, not tiles. These are documents: a row of filenames is how
 * anybody finds "the risk assessment" among nine, and a grid of identical PDF
 * glyphs is not. The toggle is still there for anyone who wants it.
 *
 * `officePreview` DELIBERATELY OFF. It renders Word and Excel through
 * Microsoft's own viewer, which means their servers fetch the file — a school
 * document naming students should not be handed to a third party to make a
 * preview slightly nicer. Download opens it in the app that owns it.
 */
export const EventDocuments: React.FC<EventDocumentsProps> = ({
	eventId,
	event,
	readOnly,
	onChange,
}) => {
	const toast = useToast();
	const { upload, uploading, sasQuery } = useEventFiles(eventId);

	const stored = useMemo<EventFile[]>(() => event.Files ?? [], [event.Files]);

	/**
	 * One signature for the whole list, fetched when the tab opens.
	 *
	 * THE DOWNLOAD CONTROL DOES NOT USE `resolveUrl`. That renderer signs the
	 * file being PREVIEWED; the list layout's download link is a plain anchor
	 * on `file.url` (read from cc-ui's CCFileGallery). These blobs are in a
	 * private container, so an unsigned URL is a 403 from Azure and the download
	 * silently did nothing. The URLs handed to the gallery therefore have to
	 * arrive already signed.
	 *
	 * ONE CALL FOR ALL OF THEM, because the signature is container-wide — see
	 * the SAS route, which is also why it is worth reading `docs/event-documents.md`
	 * on scoping these per blob. Re-fetched every time the tab is opened, since
	 * Event.tsx unmounts a tab's content when you leave it, so the hour's expiry
	 * is refreshed by the act of coming back to look.
	 */
	const [sas, setSas] = useState<string | null>(null);
	useEffect(() => {
		let cancelled = false;
		void sasQuery().then((query) => {
			if (!cancelled) setSas(query);
		});
		return () => {
			cancelled = true;
		};
	}, [sasQuery]);

	/** The separator depends on what the stored URL already carries. */
	const signed = useCallback(
		(url?: string): string | undefined => {
			if (!url) return undefined;
			if (!sas) return undefined;
			return `${url}${url.includes("?") ? "&" : "?"}${sas}`;
		},
		[sas],
	);

	/**
	 * The gallery's shape, from ours.
	 *
	 * `InternalName` is the id where it exists: `Name` is the original filename
	 * and two people attaching "Risk Assessment.pdf" would collide, which in a
	 * keyed list means one row standing in for both and Remove taking the wrong
	 * one.
	 */
	const files = useMemo<CCFile[]>(
		() =>
			stored.map((f) => ({
				id: f.InternalName ?? f.Url ?? f.Name,
				name: f.Name,
				/* Undefined until the signature lands, NOT the raw URL. The gallery
				   hides the download control when a file has no url, and a moment
				   without the control beats a control that 403s. */
				url: signed(f.Url),
				sizeBytes: f.Size,
				mimeType: f.Type,
			})),
		[stored, signed],
	);

	const onAdd = useCallback(
		async (picked: File[]) => {
			/* Checked before anything is sent, and the batch survives one bad
			   file: a drop of four where the third is a 12MB scan attaches the
			   other three and says what happened to that one. */
			const allowed: File[] = [];
			const refused: string[] = [];
			for (const file of picked) {
				try {
					checkDocument(file);
					allowed.push(file);
				} catch (error) {
					refused.push(
						error instanceof EventDocumentError
							? error.message
							: `"${file.name}" could not be attached.`,
					);
				}
			}

			const { files: attached, errors } = allowed.length
				? await upload(allowed)
				: { files: [], errors: [] };

			if (attached.length) {
				/* Appended to what is on the draft, read at call time rather than
				   captured: two uploads finishing in sequence must not both write
				   the list as it was before either of them. */
				onChange({ Files: [...(event.Files ?? []), ...attached] });
			}

			const problems = [...refused, ...errors];
			if (problems.length) {
				toast.show({
					message: problems.join(" "),
					severity: "warning",
					duration: 6000,
				});
			}
		},
		[event.Files, onChange, toast, upload],
	);

	/**
	 * Removed from the EVENT, not from storage.
	 *
	 * There is no delete here on purpose. `storage/file/` has a DELETE, but the
	 * list this edits is a draft: Discard has to put a removed file back, and a
	 * blob deleted on click cannot come back. Detaching is therefore the whole
	 * of it, and the orphan is dealt with the way `docs/event-documents.md`
	 * describes — which is also where the case for the API recording files
	 * itself is made.
	 */
	const onRemove = useCallback(
		(file: CCFile) => {
			onChange({
				Files: (event.Files ?? []).filter(
					(f) => (f.InternalName ?? f.Url ?? f.Name) !== file.id,
				),
			});
		},
		[event.Files, onChange],
	);

	/**
	 * What the preview opens.
	 *
	 * The URL it is handed is already signed above, so this returns it as it
	 * stands rather than signing it twice — appending a second query string
	 * produced a URL Azure rejects, which is the trap that made the first
	 * version of this look like it worked.
	 */
	const resolveUrl = useCallback(async (file: CCFile) => file.url ?? "", []);

	return (
		<div className="em-col-md-12 em-documents">
			<CCFileGallery
				/* The tab's heading, not just the gallery's: the tab renders none of
				   its own. Explicit rather than left to cc-ui's default, which happens
				   to be the same word — the heading the page shows should be visible
				   here rather than living in the library. */
				label="Files"
				files={files}
				onAdd={readOnly ? undefined : onAdd}
				onRemove={readOnly ? undefined : onRemove}
				resolveUrl={resolveUrl}
				accept={DOCUMENT_MIME_TYPES}
				uploading={uploading}
				defaultLayout="list"
				officePreview={false}
				emptyMessage={
					readOnly
						? "No files attached."
						: "Drop a PDF, Word, Excel, PowerPoint, CSV or text file here, or choose one. 10MB each."
				}
			/>
		</div>
	);
};
